import React from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ExternalLink } from '../../../../components';

import css from './SectionBanners.module.css';

/**
 * Campaign banners near the top of the homepage, managed by an admin on
 * /admin/banners.
 *
 * Built as a full-width band, like SectionAboutBanner directly below it. Each
 * banner picks its own side for the image, so an admin running two at once can
 * alternate them instead of stacking two identical layouts. The default is
 * image left, which mirrors the About band's text left / image right.
 *
 * The copy sits beside the image, never over it. An admin uploads whatever
 * photo they like, and a light or busy one swallows overlaid text; keeping
 * them apart means the headline is readable whatever lands there.
 *
 * Only the button is the link, as in the band below. A whole banner wrapped
 * in an anchor announces itself to a screen reader as one long link made of
 * the headline, the subtext and the label, and it makes selecting the text
 * awkward for everyone else.
 *
 * When nothing is active the section renders nothing at all, and the page
 * reads exactly as it did before the feature existed.
 *
 * @returns {JSX.Element|null}
 */
const SectionBanners = () => {
  const banners = useSelector(state => state.LandingPage?.banners) || [];

  if (banners.length === 0) {
    return null;
  }

  return (
    <section className={css.root}>
      {banners.map(banner => {
        const {
          id,
          title,
          description,
          link,
          cta,
          imagePosition,
          imageAlt,
          images,
          mobileImages,
        } = banner;
        const mobileSrc = mobileImages?.large || mobileImages?.xlarge;
        // An admin can point a banner anywhere. An internal path stays in the
        // app; anything else leaves it. NamedLink is no use here — it builds
        // its href from a route name, and this path is data.
        const isInternal = link.startsWith('/');

        // An admin can write her own button text; without one we fall back to
        // a neutral label, since the destination is whatever she chose.
        const ctaLabel = cta || <FormattedMessage id="SectionBanners.cta" />;
        const ctaLink = isInternal ? (
          <Link className={css.cta} to={link}>
            {ctaLabel}
          </Link>
        ) : (
          <ExternalLink className={css.cta} href={link}>
            {ctaLabel}
          </ExternalLink>
        );

        return (
          <article key={id} className={css.banner}>
            <div
              className={classNames(css.inner, {
                [css.innerImageRight]: imagePosition === 'right',
              })}
            >
              <picture
                className={classNames(css.imageWrapper, { [css.imageWrapperMobile]: !!mobileSrc })}
              >
                {/* The optional phone picture, below 768px. */}
                {mobileSrc ? (
                  <source
                    media="(max-width: 767px)"
                    srcSet={[
                      mobileImages.large ? `${mobileImages.large} 1200w` : null,
                      mobileImages.xlarge ? `${mobileImages.xlarge} 2400w` : null,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                    sizes="100vw"
                  />
                ) : null}
                <img
                  className={css.image}
                  src={images.xlarge || images.large}
                  srcSet={
                    images.large && images.xlarge
                      ? `${images.large} 1200w, ${images.xlarge} 2400w`
                      : undefined
                  }
                  sizes="(min-width: 768px) 44vw, 100vw"
                  // Empty alt when none was written: the headline beside it
                  // already carries the meaning, so an unlabelled image is
                  // decorative rather than missing information.
                  alt={imageAlt || ''}
                  loading="lazy"
                />
              </picture>

              <div className={css.text}>
                <h2 className={css.title}>{title}</h2>
                {description ? <p className={css.subtitle}>{description}</p> : null}
                {ctaLink}
              </div>
            </div>
          </article>
        );
      })}
    </section>
  );
};

export default SectionBanners;
